import { useEffect, useRef } from "react";
import { ArrowDown, ArrowUpRight, Bot, ChartNoAxesCombined, Code2, PanelsTopLeft } from "lucide-react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { SERVICES } from "@/config/content";
import { FinalCta } from "@/components/landing/FinalCta";
import { usePageMeta } from "@/hooks/use-page-meta";

const ICONS = [Code2, ChartNoAxesCombined, Bot, PanelsTopLeft];

const CHAPTERS = [
  {
    surface: "bg-[#f1f3ec] text-[#050606]",
    theme: "light",
    accent: "text-[#2851aa]",
    border: "border-black/15",
    muted: "text-black/55",
    grid: "solution-paper-grid",
  },
  {
    surface: "bg-[#082d32] text-white",
    theme: "dark",
    accent: "text-cyan-300",
    border: "border-white/14",
    muted: "text-white/58",
    grid: "solution-cyan-grid",
  },
  {
    surface: "bg-lime-300 text-[#050606]",
    theme: "light",
    accent: "text-black/55",
    border: "border-black/18",
    muted: "text-black/58",
    grid: "solution-dark-grid",
  },
  {
    surface: "bg-[#dfe9ff] text-[#0c1530]",
    theme: "light",
    accent: "text-[#2851aa]",
    border: "border-[#0c1530]/16",
    muted: "text-[#0c1530]/58",
    grid: "solution-blue-grid",
  },
];

const ProductVisual = ({ index }) => (
  <div className="relative h-[44svh] min-h-[360px] max-h-[590px] overflow-hidden border border-white/12 bg-[#07100e] text-white shadow-[0_28px_80px_rgba(5,6,6,.18)]" aria-hidden="true">
    <div className="system-map-grid absolute inset-0 opacity-70" />
    <div className="relative flex items-center justify-between border-b border-white/10 px-5 py-4">
      <span className="text-[9px] font-bold uppercase tracking-[.18em] text-white/40">Clube Digital / Interface</span>
      <div className="flex gap-1.5"><i className="h-1.5 w-1.5 bg-cyan-300" /><i className="h-1.5 w-1.5 bg-lime-300" /></div>
    </div>

    {index === 0 && (
      <div className="relative grid h-[calc(100%_-_53px)] grid-cols-[.32fr_.68fr]">
        <div className="border-r border-white/10 p-4 sm:p-6">
          <p className="text-[8px] font-bold uppercase tracking-[.18em] text-white/30">Operação</p>
          {["Visão geral", "Pedidos", "Equipe", "Indicadores"].map((item, itemIndex) => <div key={item} className={`mt-4 border-l-2 px-3 py-2 text-[9px] uppercase tracking-[.1em] ${itemIndex === 0 ? "border-lime-300 bg-white/[.06] text-white" : "border-transparent text-white/35"}`}>{item}</div>)}
        </div>
        <div className="p-4 sm:p-7">
          <div className="grid grid-cols-2 gap-3">
            {["Fluxos ativos", "Pendências", "Tempo médio", "Eficiência"].map((item, itemIndex) => <div key={item} className="border border-white/10 bg-white/[.035] p-3 sm:p-4"><p className="text-[8px] uppercase tracking-[.14em] text-white/35">{item}</p><p className="mt-3 text-2xl font-semibold">{[18, 4, "2h", "91%"][itemIndex]}</p></div>)}
          </div>
          <div className="mt-4 flex h-28 items-end gap-2 border border-white/10 p-4 sm:h-36">
            {[42, 68, 51, 82, 61, 92, 74].map((height) => <span key={height} className="flex-1 bg-lime-300/75" style={{ height: `${height}%` }} />)}
          </div>
        </div>
      </div>
    )}

    {index === 1 && (
      <div className="relative grid h-[calc(100%_-_53px)] grid-cols-3 gap-3 p-4 sm:p-7">
        {["Novos", "Em conversa", "Propostas"].map((title, column) => <div key={title} className="border border-white/10 bg-white/[.025] p-3"><p className="border-b border-white/10 pb-3 text-[9px] font-bold uppercase tracking-[.14em] text-white/45">{title}</p>{Array.from({ length: column + 2 }).map((_, card) => <div key={card} className="mt-3 border border-white/10 bg-[#0b1d20] p-3"><span className="block h-1.5 bg-white/22" style={{ width: `${78 - card * 11}%` }} /><span className="mt-3 block h-1 bg-cyan-300/45" style={{ width: `${38 + column * 12}%` }} /></div>)}</div>)}
      </div>
    )}

    {index === 2 && (
      <div className="relative mx-auto flex h-[calc(100%_-_53px)] max-w-md flex-col justify-center gap-3 px-5 sm:px-8">
        {["Mensagem recebida", "Intenção identificada", "Dados organizados", "Equipe acionada"].map((label, step) => <div key={label} className="flex items-center gap-4 border border-white/10 bg-white/[.04] p-4"><span className="grid h-9 w-9 shrink-0 place-items-center border border-cyan-300/35 text-[10px] text-cyan-300">0{step + 1}</span><span className="text-sm text-white/70">{label}</span><span className="ml-auto h-2 w-2 bg-lime-300" /></div>)}
      </div>
    )}

    {index === 3 && (
      <div className="relative flex h-[calc(100%_-_53px)] items-center justify-center p-5 sm:p-8">
        <div className="grid h-full w-full max-w-[620px] grid-rows-[36px_1fr] border border-white/14 bg-[#edf2ff] text-[#0c1530] shadow-[14px_14px_0_rgba(0,210,255,.13)]">
          <div className="flex items-center gap-2 border-b border-black/10 px-3"><span className="h-2 w-2 bg-red-300" /><span className="h-2 w-2 bg-amber-300" /><span className="h-2 w-2 bg-green-300" /></div>
          <div className="grid place-items-center p-5 text-center"><div><p className="text-[9px] font-bold uppercase tracking-[.18em] text-[#2851aa]">Presença digital</p><p className="display-title mt-4 text-4xl leading-none sm:text-6xl">Clara em qualquer tela.</p><span className="mx-auto mt-7 block h-1 w-24 bg-[#2851aa]" /></div></div>
        </div>
      </div>
    )}
  </div>
);

const ServiceChapter = ({ service, index }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const style = CHAPTERS[index];
  const Icon = ICONS[index];
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const visualY = useTransform(scrollYProgress, [0, 1], [reduce ? "0%" : "7%", reduce ? "0%" : "-7%"]);
  const titleX = useTransform(scrollYProgress, [0, 0.5, 1], [reduce ? 0 : 32, 0, reduce ? 0 : -18]);

  return (
    <article ref={ref} id={service.slug} data-header-theme={style.theme} className={`relative scroll-mt-20 overflow-hidden ${style.surface}`} data-testid={`service-detail-${index}`}>
      <div className={`pointer-events-none absolute inset-0 ${style.grid} opacity-60`} aria-hidden="true" />
      <div className="site-shell relative grid min-h-[100svh] content-center gap-14 py-28 lg:grid-cols-[.82fr_1.18fr] lg:items-center lg:gap-20 lg:py-32">
        <div>
          <div className={`flex items-center justify-between border-b pb-5 ${style.border}`}>
            <p className={`text-[10px] font-bold uppercase tracking-[.2em] ${style.accent}`}>Solução sob medida</p>
            <Icon size={21} strokeWidth={1.6} />
          </div>
          <motion.h2 style={{ x: titleX }} className="display-title mt-10 text-[clamp(3.15rem,6.1vw,6.7rem)] leading-[.9]">{service.title}</motion.h2>
          <p className={`mt-8 max-w-xl text-base leading-relaxed md:text-lg ${style.muted}`}>{service.detail}</p>
          <div className={`mt-10 flex flex-wrap gap-x-6 gap-y-3 border-y py-5 ${style.border}`}>
            {service.tags.map((tag) => <span key={tag} className="text-[10px] font-bold uppercase tracking-[.16em]">{tag}</span>)}
          </div>
          <Link to="/contato" className="group mt-9 inline-flex items-center gap-4 text-sm font-bold">
            Conversar sobre esta solução
            <span className={`grid h-10 w-10 place-items-center border transition-transform group-hover:-translate-y-1 group-hover:translate-x-1 ${style.border}`}><ArrowUpRight size={17} /></span>
          </Link>
        </div>
        <motion.div style={{ y: visualY }}><ProductVisual index={index} /></motion.div>
      </div>
    </article>
  );
};

export const ServicesPage = () => {
  usePageMeta("Serviços", "Sistemas sob medida, CRM, automação, inteligência artificial, sites e experiências web.");
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView({ block: "start" }));
  }, [location.hash]);

  return (
    <main>
      <section data-header-theme="dark" className="editorial-grid relative flex min-h-[100svh] items-end overflow-hidden bg-[#050606] pb-14 pt-36 text-white md:pb-20 md:pt-44">
        <div className="site-shell relative">
          <div className="flex items-center justify-between gap-8 border-b border-white/12 pb-6">
            <p className="eyebrow">Serviços</p>
            <p className="hidden text-[10px] font-bold uppercase tracking-[.18em] text-white/32 sm:block">Do problema à solução</p>
          </div>
          <h1 className="display-title mt-12 max-w-[1280px] text-[clamp(3.6rem,8.8vw,9rem)] leading-[.88]">Cada gargalo pede uma resposta diferente.</h1>
          <div className="mt-14 grid gap-10 border-t border-white/12 pt-7 lg:grid-cols-[.8fr_1.2fr] lg:items-end">
            <p className="max-w-xl text-base leading-relaxed text-white/55 md:text-lg">Não começamos por uma ferramenta. Começamos pelo que precisa funcionar melhor e construímos a tecnologia ao redor disso.</p>
            <nav className="grid gap-px border border-white/12 bg-white/12 sm:grid-cols-2" aria-label="Soluções">
              {SERVICES.map((service) => <a key={service.slug} href={`#${service.slug}`} className="group flex min-h-14 items-center justify-between bg-[#050606] px-4 text-xs font-bold transition-colors hover:bg-white/[.05] hover:text-lime-300"><span>{service.title}</span><ArrowDown size={14} className="transition-transform group-hover:translate-y-1" /></a>)}
            </nav>
          </div>
        </div>
      </section>

      {SERVICES.map((service, index) => <ServiceChapter key={service.slug} service={service} index={index} />)}
      <FinalCta />
    </main>
  );
};
